/**
 * Cache invalidation helpers (filiale context switch + permission refresh).
 *
 * When the user changes filiale context (mode / codes / dept), all cached
 * queries that depend on the context must be refetched so the next request
 * carries the new X-Context-Filiales / X-Context-Dept headers.
 *
 * Uses invalidateQueries (NOT removeQueries): removeQueries silently orphans
 * active observers in TanStack Query v5 (the cache entry is dropped but the
 * mounted hook keeps its stale reference and never refetches — see
 * TanStack/query#8597). invalidateQueries marks matching queries stale and
 * refetches active ones in the background; useTable's placeholderData keeps
 * the previous rows visible during the refetch, so there is no flash.
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/api/queryKeys";
import { queryClient } from "@/main";

/**
 * Denylist: queries that must NOT be invalidated on context change.
 * Auth, users, permissions, notifications, tickets, security are global
 * and unaffected by filiale selection.
 */
const CONTEXT_INVARIANT_KEYS = [
  "auth",
  "users",
  "permissions",
  "notifications",
  "tickets",
  "security",
  "collection",
];

/**
 * Invalidate all cached queries except denylisted keys.
 * Active queries refetch immediately with the new context headers.
 */
export function invalidateFilialeContext(): void {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      return !CONTEXT_INVARIANT_KEYS.includes(key as string);
    },
  });
}

/**
 * Invalidate the permission query branch after a mutation or a Mercure signal.
 *
 * `permissions.all` (`["permissions"]`) is the prefix of `permissions.current`
 * and of every `permissions.byUser(id)` entry, so invalidating it refetches
 * the whole branch — a profile edit can touch an unknown set of users, hence
 * the prefix is always invalidated. The explicit `byUser` call documents the
 * targeted recipient and keeps the refetch scoped if `all` ever narrows.
 *
 * `refetchType: "active"` (explicit, though it is the default): the AuthProvider
 * permission query stays mounted for the whole session, so it must refetch now
 * instead of waiting for a reload — the Zustand store and the query cache are
 * two sources of truth and must move together.
 */
export function invalidateUserPermissions(
  queryClient: QueryClient,
  userId?: number | string,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.permissions.all,
    refetchType: "active",
  });

  if (userId === undefined) return;

  queryClient.invalidateQueries({
    queryKey: queryKeys.permissions.byUser(userId),
    refetchType: "active",
  });
}
